import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../redux/companyForm";
import { setIsFormComplete } from "../../redux/globalFunctions";
import translations from "../../en.json";
import translationsNo from "../../no.json";

export function CompanyDataQuestions() {
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.companyForm);
  const { language } = useSelector((state) => state.global);

  const pageText =
    language === "en"
      ? translations.companyDataQuestions
      : translationsNo.companyDataQuestions;

  const [visibleOptions, setVisibleOptions] = useState(
    pageText.industry.options
  );
  const [isSelected, setIsSelected] = useState(false);
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);

  const handleFieldChange = (field, value) => {
    dispatch(updateField({ field, value }));
  };

  useEffect(() => {
    const { companyName, industry, companySize, annualRevenue, locations } =
      formData;
    const isFormIncomplete =
      !companyName || !industry || !companySize || !annualRevenue || !locations;

    dispatch(setIsFormComplete(!isFormIncomplete));
  }, [formData, dispatch]);

  function setMultipleAnswers(option) {
    setIsSelected(true);
    textareaRef.current.focus();

    const currentValue = formData.industry || "";
    const updatedOptions = currentValue.split(", ").filter(Boolean);

    if (!updatedOptions.includes(option)) {
      updatedOptions.push(option);
    }
    const updatedValue = updatedOptions.join(", ");
    dispatch(updateField({ field: "industry", value: updatedValue }));

    setVisibleOptions((prevOptions) =>
      prevOptions.filter((item) => item !== option)
    );
  }

  function handleTextareaClick() {
    setIsSelected(true);
  }

  function handleTextareaBlur() {
    setTimeout(() => {
      setIsSelected(false);
    }, 100);
  }

  const debounceTimeout = useRef(null);

  function handleTextareaChange(e) {
    const textVal = e.target.value;
    setValue(textVal);

    clearTimeout(debounceTimeout.current);

    // Set a new timeout to dispatch the update after 500ms
    debounceTimeout.current = setTimeout(() => {
      if (textVal === "") {
        dispatch(updateField({ field: "industry", value: "" }));
        setVisibleOptions(pageText.industry.options);
      } else {
        dispatch(updateField({ field: "industry", value: textVal }));
      }
    }, 10); // Adjust the delay time as needed
  }

  return (
    <div>
      {/* Company Name */}
      <div className="question">
        <div className="input-container">
          <label htmlFor="companyName">{pageText.companyName.label}</label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          />
        </div>
      </div>

      {/* Industry */}
      <div className="question">
        <div className="select-container">
          <label htmlFor="industry">{pageText.industry.label}</label>
          <textarea
            id="industry"
            name="industry"
            value={formData.industry !== undefined ? formData.industry : value}
            ref={textareaRef}
            className={isSelected ? "focused" : "notFocused"}
            placeholder={pageText.industry.placeholder}
            onChange={(e) => handleTextareaChange(e)}
            onClick={handleTextareaClick}
            onBlur={handleTextareaBlur}
          ></textarea>
          {visibleOptions.map((option, index) => (
            <li
              className="suggestion-chip"
              id={option}
              onMouseDown={(e) => e.preventDefault()}
              key={`option-${index}`}
              onClick={() => setMultipleAnswers(option)}
            >
              {option}
            </li>
          ))}
        </div>
      </div>

      {/* Company Size */}
      <div className="question">
        <div className="select-container">
          <label htmlFor="companySize">{pageText.companySize.label}</label>
          <select
            id="companySize"
            name="companySize"
            value={formData.companySize}
            onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          >
            <option value="" disabled>
              {pageText.companySize.placeholder}
            </option>
            {pageText.companySize.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Annual Revenue */}
      <div className="question">
        <div className="select-container">
          <label htmlFor="annualRevenue">{pageText.annualRevenue.label}</label>
          <select
            id="annualRevenue"
            name="annualRevenue"
            value={formData.annualRevenue}
            onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          >
            <option value="" disabled>
              {pageText.annualRevenue.placeholder}
            </option>
            {pageText.annualRevenue.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Locations */}
      <div className="question">
        <p>{pageText.locations.label}</p>
        <div className="input-container">
          <label htmlFor="locations">{pageText.locations.inputLabel}</label>
          <input
            type="text"
            id="locations"
            name="locations"
            value={formData.locations}
            onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
